import React, { useCallback, useId } from 'react';
import styles from './rotation-content.module.scss';
import { notification } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil } from '@fortawesome/pro-solid-svg-icons';
import { Button, IconButton } from 'src/components/design-system';
import { TextInput } from 'src/components/design-system/forms';
import * as Yup from 'yup';
import { useUpdateRotation } from 'shared/hooks/rotations.hooks';
import { useRotationsContext } from '../rotations-context';
import { usePageTitle } from 'src/components/app/use-page-title';
import { AcgmeBadge } from '../components/acgme-badge.component';

const name = Yup.string().min(2).required('Required!');

const RotationName = () => {
  const {
    lang,
    selectedRotation: rotation,
    canEdit,
    loadingRotation,
    analyticsTriggers,
  } = useRotationsContext();
  const updateRotation = useUpdateRotation();
  const [editing, setEditing] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [newName, setNewName] = React.useState(rotation?.name || '');
  const [api, contextHolder] = notification.useNotification();
  const acgmeBadgeId = useId();

  const { updateSubtitle } = usePageTitle();

  React.useEffect(() => {
    updateSubtitle(rotation?.name);
  }, [rotation?.name, updateSubtitle]);

  const handleValidateName = useCallback(async e => {
    setError(null);
    setNewName(e.target.value);

    try {
      await name.validate(e.target.value);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  const showToast = useCallback(
    message => {
      api.error({
        title: message || lang.ERROR_OCCURRED,
        placement: 'bottomRight',
      });
    },
    [api, lang.ERROR_OCCURRED],
  );

  const handleCancel = useCallback(() => {
    setEditing(false);
    setNewName(rotation?.name || '');
  }, [rotation]);

  const handleNameChange = useCallback(async () => {
    try {
      await updateRotation.mutateAsync({
        rotationId: rotation.id,
        payload: { name: newName },
      });
      analyticsTriggers?.onRotationDetailsUpdated({ id: rotation.id, name: newName });
    } catch (e) {
      showToast(e.response?.data?.message || e.message);
    } finally {
      setEditing(false);
    }
  }, [newName, rotation, showToast, updateRotation, analyticsTriggers]);

  if (loadingRotation) {
    return null;
  }

  return editing ? (
    <div className={styles.editName}>
      {contextHolder}
      <TextInput
        label={lang.ROTATION_NAME}
        value={newName}
        onChange={handleValidateName}
        size="large"
        style={{ maxWidth: '70%' }}
        error={error}
        required
      />
      <div className={styles.actionButtons}>
        <Button type="primary" onClick={handleNameChange} disabled={!!error}>
          {lang.SUBMIT}
        </Button>
        <Button onClick={handleCancel}>{lang.CANCEL}</Button>
      </div>
    </div>
  ) : (
    <div className={styles.title}>
      {/* The badge is a visual sibling; describedby announces it with the heading. */}
      <h1
        className={styles.titleText}
        aria-describedby={rotation?.isAcgmeRequired ? acgmeBadgeId : undefined}>
        {rotation?.name}
      </h1>
      {rotation?.isAcgmeRequired && <AcgmeBadge size="large" id={acgmeBadgeId} />}

      {canEdit && (
        <IconButton
          label={lang.EDIT_NAME}
          onClick={() => setEditing(true)}
          className={styles.titleIconButton}>
          <FontAwesomeIcon icon={faPencil} className={styles.titleIcon} />
        </IconButton>
      )}
    </div>
  );
};

export default RotationName;
